import { useEffect, useRef, useState } from 'react'

import { MIN_PROGRESS_INCREMENT, MIN_PROGRESS_UPDATE_INTERVAL } from '../../constants'
import { UploadItem } from '../../types'

interface UseProgressAnimationProps {
  activeUploads: UploadItem[]
  allSuccess: boolean
  allCompleted: boolean
}

export const useProgressAnimation = ({ activeUploads, allSuccess, allCompleted }: UseProgressAnimationProps) => {
  const [displayedProgress, setDisplayedProgress] = useState(0)
  const [instantReset, setInstantReset] = useState(false)
  const prevActiveCountRef = useRef(0)
  const prevActiveUploadIdsRef = useRef<Set<string>>(new Set())
  const prevAllCompletedRef = useRef(false)

  const overallProgress =
    activeUploads.length === 0
      ? allSuccess
        ? 100
        : 0
      : activeUploads.length === 1
        ? activeUploads[0].displayProgress
        : (() => {
            const totalProgress = activeUploads.reduce((sum, upload) => {
              const fileSize = upload.file?.size || 1
              return sum + upload.displayProgress * fileSize
            }, 0)
            const totalSize = activeUploads.reduce((sum, upload) => sum + (upload.file?.size || 1), 0)
            return totalSize > 0 ? Math.round(totalProgress / totalSize) : 0
          })()

  useEffect(() => {
    const currentActiveIds = new Set(activeUploads.map((u) => u.id))
    const prevActiveIds = prevActiveUploadIdsRef.current
    const currentActiveCount = activeUploads.length
    const prevActiveCount = prevActiveCountRef.current

    const hasNewUploads =
      currentActiveIds.size > prevActiveIds.size || Array.from(currentActiveIds).some((id) => !prevActiveIds.has(id))

    const wasZero = prevActiveCount === 0
    const isNowGreaterThanZero = currentActiveCount > 0
    const wasAllCompleted = prevAllCompletedRef.current
    const isNowAllCompleted = allCompleted && currentActiveCount === 0

    if (isNowGreaterThanZero && wasZero) {
      setInstantReset(true)
      setDisplayedProgress(0)
      prevAllCompletedRef.current = false
      setTimeout(() => setInstantReset(false), 0)
    } else if (hasNewUploads && wasAllCompleted && isNowGreaterThanZero) {
      setInstantReset(true)
      setDisplayedProgress(0)
      prevAllCompletedRef.current = false
      setTimeout(() => setInstantReset(false), 0)
    } else if (hasNewUploads && currentActiveCount > 0 && prevActiveCount > 0 && !wasAllCompleted) {
      const currentOverallProgress =
        activeUploads.length === 1
          ? activeUploads[0].displayProgress
          : activeUploads.reduce((sum, u) => sum + u.displayProgress * (u.file?.size || 1), 0) /
            activeUploads.reduce((sum, u) => sum + (u.file?.size || 1), 0)

      if (displayedProgress > currentOverallProgress) {
        setDisplayedProgress(currentOverallProgress)
      }
    }

    if (isNowAllCompleted) {
      prevAllCompletedRef.current = true
    } else if (isNowGreaterThanZero) {
      prevAllCompletedRef.current = false
    }

    prevActiveCountRef.current = currentActiveCount
    prevActiveUploadIdsRef.current = currentActiveIds
  }, [activeUploads, displayedProgress, allCompleted])

  useEffect(() => {
    if (activeUploads.length === 0) {
      return
    }

    if (displayedProgress >= overallProgress) {
      return
    }

    const interval = setInterval(() => {
      setDisplayedProgress((prev) => {
        const next = Math.min(prev + MIN_PROGRESS_INCREMENT, overallProgress)
        return next
      })
    }, MIN_PROGRESS_UPDATE_INTERVAL)

    return () => clearInterval(interval)
  }, [displayedProgress, overallProgress, activeUploads.length])

  useEffect(() => {
    if (activeUploads.length === 0) {
      return
    }

    if (overallProgress < displayedProgress) {
      setDisplayedProgress(overallProgress)
    }
  }, [overallProgress, displayedProgress, activeUploads.length])

  useEffect(() => {
    if (activeUploads.length === 0 && !allSuccess) {
      setInstantReset(true)
      setDisplayedProgress(0)
      setTimeout(() => setInstantReset(false), 0)
    } else if (allSuccess) {
      setDisplayedProgress(100)
    }
  }, [activeUploads.length, allSuccess])

  return {
    displayedProgress,
    instantReset,
    overallProgress
  }
}
